import { getFirestore } from '../firebase.js';
import { collection, addDoc, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';

// Get Firestore instance
const db = getFirestore();

// Collection reference
const subscriptionsRef = collection(db, 'subscriptions');

/**
 * Subscribe a user to a streamer
 * @param {string} subscriberId - The ID of the subscribing user
 * @param {string} streamerId - The ID of the streamer
 * @param {string} tier - The subscription tier
 * @param {number} price - The subscription price
 * @param {object} streamerData - The streamer data including tier status
 * @returns {Promise<object>} - The subscription data
 */
export const createSubscription = async (subscriberId, streamerId, tier, price, streamerData) => {
  try {
    // Determine revenue share based on streamer tier
    const isTopStreamer = streamerData.tier === 'verified';
    const streamerShare = isTopStreamer ? 0.98 : 0.90;
    const platformShare = 1 - streamerShare;
    
    // Calculate shares
    const streamerAmount = price * streamerShare;
    const platformAmount = price * platformShare;
    
    const subscriptionData = {
      subscriberId,
      streamerId,
      tier,
      price,
      streamerShare,
      platformShare,
      streamerAmount,
      platformAmount,
      isActive: true,
      subscribedAt: serverTimestamp(),
      renewalDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      lastUpdated: serverTimestamp()
    };
    
    const docRef = await addDoc(subscriptionsRef, subscriptionData);
    return { id: docRef.id, ...subscriptionData };
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

/**
 * Get subscriptions for a streamer
 * @param {string} streamerId - The ID of the streamer
 * @returns {Promise<Array>} - Array of subscription data
 */
export const getStreamerSubscriptions = async (streamerId) => {
  try {
    const q = query(subscriptionsRef, where('streamerId', '==', streamerId), where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching streamer subscriptions:', error);
    throw error;
  }
};

/**
 * Get subscriptions for a user
 * @param {string} subscriberId - The ID of the subscriber
 * @returns {Promise<Array>} - Array of subscription data
 */
export const getUserSubscriptions = async (subscriberId) => {
  try {
    const q = query(subscriptionsRef, where('subscriberId', '==', subscriberId), where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching user subscriptions:', error);
    throw error;
  }
};

/**
 * Check if a user is subscribed to a streamer
 * @param {string} subscriberId - The ID of the subscriber
 * @param {string} streamerId - The ID of the streamer
 * @returns {Promise<boolean>} - Whether the user is subscribed
 */
export const isUserSubscribed = async (subscriberId, streamerId) => {
  try {
    const q = query(
      subscriptionsRef, 
      where('subscriberId', '==', subscriberId),
      where('streamerId', '==', streamerId),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    throw error;
  }
};

/**
 * Cancel a subscription
 * @param {string} subscriptionId - The ID of the subscription
 * @returns {Promise<void>}
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    const subscriptionRef = doc(db, 'subscriptions', subscriptionId);
    await updateDoc(subscriptionRef, {
      isActive: false,
      cancelledAt: serverTimestamp(),
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw error;
  }
};

/**
 * Get subscription tiers and pricing
 * @returns {Object} - Subscription tiers and pricing
 */
export const getSubscriptionTiers = () => {
  return {
    'tier1': {
      name: 'Tier 1',
      price: 4.99,
      benefits: [
        'Ad-free viewing',
        'Subscriber badge',
        'Channel emotes'
      ]
    },
    'tier2': {
      name: 'Tier 2',
      price: 9.99,
      benefits: [
        'All Tier 1 benefits',
        'Custom emotes',
        'Priority chat visibility'
      ]
    },
    'tier3': {
      name: 'Tier 3',
      price: 24.99,
      benefits: [
        'All Tier 2 benefits',
        'Exclusive badges',
        'Subscriber-only streams access'
      ]
    }
  };
}; 